import { deadlineUrgency } from '../dossiers/dossierListDerive';

export type TaskTableColumn = 'due' | 'subject' | 'owner' | 'status' | 'dossier';
export type TaskSortDir = 'asc' | 'desc';

export type TaskTableRow = {
  id: string;
  subject: string;
  due: string | null;
  ownerId: string | null;
  owner: string;
  done: boolean;
  dossierId: string | null;
  dossier: string;
};

export type TaskEcheanceFilter = 'overdue' | 'week' | 'none';

export type TaskTableFilters = {
  status: 'open' | 'done' | null;
  ownerId: string | null;
  echeance: TaskEcheanceFilter | null;
  dossierId: string | null;
};

const EMPTY: TaskTableFilters = {
  status: null,
  ownerId: null,
  echeance: null,
  dossierId: null,
};

function textRank(a: string, b: string, dir: TaskSortDir): number {
  const ae = a.trim() === '';
  const be = b.trim() === '';
  if (ae && be) return 0;
  if (ae) return 1;
  if (be) return -1;
  const cmp = a.localeCompare(b, 'fr', { sensitivity: 'base' });
  return dir === 'asc' ? cmp : -cmp;
}

function dueRank(a: string | null, b: string | null, dir: TaskSortDir): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const cmp = new Date(a).getTime() - new Date(b).getTime();
  if (cmp === 0) return 0;
  const signed = cmp > 0 ? 1 : -1;
  return dir === 'asc' ? signed : -signed;
}

export function compareTaskRows(
  a: TaskTableRow,
  b: TaskTableRow,
  column: TaskTableColumn,
  dir: TaskSortDir,
): number {
  let cmp = 0;
  if (column === 'due') cmp = dueRank(a.due, b.due, dir);
  else if (column === 'subject') cmp = textRank(a.subject, b.subject, dir);
  else if (column === 'owner') cmp = textRank(a.owner, b.owner, dir);
  else if (column === 'dossier') cmp = textRank(a.dossier, b.dossier, dir);
  else {
    const rank = (row: TaskTableRow) => (row.done ? 1 : 0);
    const diff = rank(a) - rank(b);
    cmp = dir === 'asc' ? diff : -diff;
  }
  if (cmp !== 0) return cmp;
  return a.subject.localeCompare(b.subject, 'fr', { sensitivity: 'base' });
}

export function sortTaskRows(
  rows: TaskTableRow[],
  column: TaskTableColumn,
  dir: TaskSortDir,
): TaskTableRow[] {
  return [...rows].sort((a, b) => compareTaskRows(a, b, column, dir));
}

export function matchesTaskTableFilters(
  row: TaskTableRow,
  filters: TaskTableFilters = EMPTY,
  now: Date = new Date(),
): boolean {
  if (filters.status === 'open' && row.done) return false;
  if (filters.status === 'done' && !row.done) return false;
  if (filters.ownerId && row.ownerId !== filters.ownerId) return false;
  if (filters.dossierId && row.dossierId !== filters.dossierId) return false;
  if (!filters.echeance) return true;
  if (filters.echeance === 'none') return row.due === null;
  if (!row.due) return false;
  const urgency = deadlineUrgency(row.due, now);
  if (filters.echeance === 'overdue') return urgency === 'overdue';
  return urgency === 'urgent';
}
